import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PulloverSelect, withStyles } from 'vopay';
import styles from './OrganizationChartControls.module.scss';

const cn = withStyles(styles);

/** As the account portal's org chart offers them. */
const DISPLAY_LEVEL_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100];

type OrganizationChartControlsProps = {
  displayLevel: number;
  onDisplayLevelChange: (displayLevel: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  /** The row's right-hand side: the Overview's Show and Status filters. The chart views keep theirs in the filter bar. */
  children?: ReactNode;
};

/** The control row above the canvas (spec 6.3): Display Levels and Show Up To, then the host's filters. */
export default function OrganizationChartControls({
  displayLevel,
  onDisplayLevelChange,
  pageSize,
  onPageSizeChange,
  children,
}: OrganizationChartControlsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationChartControls' });

  return (
    <div className={cn('controls')} data-testid="organizationChartControls">
      {/* Figma `Header › Settings`: Inter Medium 14/24 labels around the counters. */}
      <div className={cn('settings')}>
        <div className={cn('setting')}>
          <span className={cn('label')}>{t('displayPrefix', { defaultValue: 'Display' })}</span>

          <PulloverSelect
            name="displayLevel"
            value={String(displayLevel)}
            onChange={(value) => onDisplayLevelChange(Number(value))}
          >
            {DISPLAY_LEVEL_OPTIONS.map((level) => (
              <PulloverSelect.Option key={level} value={String(level)}>
                {level}
              </PulloverSelect.Option>
            ))}
          </PulloverSelect>

          <span className={cn('label')}>{t('levelsSuffix', { defaultValue: 'Level(s)' })}</span>
        </div>

        <div className={cn('setting')}>
          <span className={cn('label')}>{t('showUpToPrefix', { defaultValue: 'Show Up To' })}</span>

          <PulloverSelect
            name="pageSize"
            value={String(pageSize)}
            onChange={(value) => onPageSizeChange(Number(value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <PulloverSelect.Option key={size} value={String(size)}>
                {size}
              </PulloverSelect.Option>
            ))}
          </PulloverSelect>

          <span className={cn('label')}>{t('entitiesSuffix', { defaultValue: 'Entities' })}</span>
        </div>
      </div>

      {children && <div className={cn('filters')}>{children}</div>}
    </div>
  );
}
